import { format } from 'date-fns';
import { useIsReadOnly, useMonthlyReportStore } from '../../../store/reportStore';
import { getCommaSeparatedNumber } from '../../../utils/common';
import { useEffect } from 'react';
import { initCalendar, mapSubmittedDxDataToCalendar } from '../../../utils/calendar';
import { getReportDxData } from '../../../data/mysql';

const WorkCalendar = () => {
  const { reportMxData, reportDxData, selectedMonth, setReportDxData } = useMonthlyReportStore();
  const isReadOnly = useIsReadOnly(); // 제출된 보고서는 수정 불가능하다.

  /**
   * 임시 일별(日)이 존재한다면 ReportDxData에 임시 일(日) 데이터를 넣는다.
   * 1. 제출된 보고서는 제출된 dx 데이터로 채움
   * 2. 저장된 데이터가 있으면 복원
   * 3. 저장된 데이터가 없다면 초기화
   */
  useEffect(() => {
    // 1. 제출된 보고서는 제출된 dx 데이터로 채움
    if (reportMxData.is_submitted === 'Y' && reportMxData.id) {
      const update = async () => {
        const submittedDxData = await getReportDxData({ reportId: reportMxData.id });
        const calendar = mapSubmittedDxDataToCalendar(selectedMonth, new Date(), submittedDxData);
        setReportDxData(calendar);
      };

      update();
      return;
    }

    // 2. 임시 저장된 데이터가 있으면 복원
    if (reportMxData.saved_dx_data) {
      const savedData = JSON.parse(reportMxData.saved_dx_data);
      setReportDxData(savedData);
      return;
    }

    // 3. 저장된 데이터가 없으면 새 캘린더 초기화
    setReportDxData(initCalendar(selectedMonth, new Date()));
  }, [reportMxData.dt, reportMxData.is_submitted, reportMxData.saved_dx_data, selectedMonth, setReportDxData]);

  /**
   * 해당 요일(월화수목금안)을 누르면 해당 달의 모든 요일(column)이 선택된다.
   * : 해당 요일을 선택했을 경우
   */
  const handleClickDayRow = ({ dayRowIdx }) => {
    if (isReadOnly) return;
    const checkState = !reportDxData // 모든 날이 선택 되어있으면 false, 모두 선택되어 있지 않으면 true
      .filter((week) => week[dayRowIdx].isThisMonth) // 해당 달이 아닌 것은 검사 대상에서 뺀다.
      .every((week) => week[dayRowIdx].checked); // 모두 체크가 되어 있는가?

    setReportDxData(
      reportDxData.map((week) =>
        week.map((day, dayIdx) =>
          dayIdx === dayRowIdx && day.isThisMonth //
            ? { ...day, checked: checkState }
            : day,
        ),
      ),
    );
  };

  /** 해당 날을 하나씩 눌렀을 때, 해당 날이 선택(checked) 된다. */
  const handleClickDay = ({ weekIdx, dayIdx }) => {
    if (isReadOnly) return;
    setReportDxData(
      reportDxData.map((week, i) =>
        i !== weekIdx
          ? week
          : week.map(
              (day, j) =>
                j === dayIdx && day.isThisMonth //
                  ? { ...day, checked: !day.checked } //
                  : day, //
            ),
      ),
    );
  };

  return (
    <>
      <div className="row">
        <div className="work-calendar">
          {/* Header ------- */}
          <div className="header">
            <div className="first">
              <span className="text">
                <span className="text month">{format(selectedMonth, 'M')}월</span>
                {format(selectedMonth, 'yyyy')}
              </span>
            </div>
          </div>
          {/* Days [월, 화, 수, 목, 금, 토] ------- */}
          <div className="days">
            {Array.from(['일', '월', '화', '수', '목', '금', '안'], (day, dayRowIdx) => {
              return (
                <div
                  className={`text col ${isReadOnly ? 'disabled' : ''}`}
                  key={dayRowIdx}
                  onClick={isReadOnly ? undefined : () => handleClickDayRow({ dayRowIdx })}
                  style={isReadOnly ? { opacity: 0.6 } : {}}
                >
                  {day}
                </div>
              );
            })}
          </div>
          {/* Body 달력 내용 -------
                [  1,  2,  3,  4,  5,  6,  7]
                [  7,  8,  9, 10, 11, 12, 13]
                [ 14, 15, 16, 17, 18, 19, 20]
                [ 21, 22, 23, 24, 25, 26, 27]
                [ 28, 29, 30, 31,   ,   ,   ]
            */}

          <div className="body">
            {reportDxData?.map((week, weekIdx) => {
              return (
                <div className="row" key={weekIdx}>
                  {week.map((day, dayIdx) => {
                    return (
                      <div
                        key={dayIdx}
                        className={`col ${
                          !day.isThisMonth //
                            ? 'disabled'
                            : day.isToday //
                            ? 'today'
                            : 'valid'
                        } ${
                          day.checked //
                            ? 'checked'
                            : ''
                        } ${isReadOnly ? 'disabled' : ''}`}
                        onClick={!day.isThisMonth || isReadOnly ? undefined : () => handleClickDay({ weekIdx, dayIdx })}
                        style={!day.isThisMonth ? { cursor: 'default' } : isReadOnly ? { cursor: 'default', opacity: 0.6 } : {}}
                      >
                        <span className={`day ${!day.isThisMonth ? 'inactive' : ''}`}>{day.day}</span>
                        <div className="content">
                          {day.seekerCnt ? <span>방문(구도자): {day.seekerCnt}</span> : ''}
                          {day.memberCnt ? <span>방문(신자): {day.memberCnt}</span> : ''}
                          {day.teachCnt ? <span>성경교수: {day.teachCnt}</span> : ''}
                          {day.sermonCnt ? <span>설교한수: {day.sermonCnt}</span> : ''}
                          {day.transportation ? <span>교통비: {getCommaSeparatedNumber(day.transportation)}원</span> : ''}
                          {day.activities ? <span>활동내역: {day.activities}</span> : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        {/* controls ------- */}
      </div>
    </>
  );
};

export default WorkCalendar;
